<template>
    <Spinner v-if="displaySpinner" />
    <InfoMessage
        title="Welcome to your product or service"
        storageKey="welcome-message-product"
        :forceShow="!hasAnyContent"
        showToggle
    >
        <p>
            Products and services are what the domain offers to the outside
            world. This can be to other domains within your organization, or
            externally. It's realized by executing processes.
        </p>
        <p v-if="processes.length === 0">
            Start by creating your first process. Processes define steps that
            users need to perform to achieve a business goal.
        </p>
    </InfoMessage>
    <Page>
        <template #title>
            <EditableTitle v-model="product" icon="pi pi-box" />
        </template>

        <template #controls>
            <LastChangedInfo
                v-if="product?.identity"
                :entityId="product.identity"
                :entityType="EntityType.PRODUCT"
            />
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                severity="secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateProductDescription"
            />
        </template>
        <Section class="description" editable>
            <TextAppArtefact
                v-if="product?.identity && description"
                v-model="description"
                :entityId="product.identity"
                :entityType="EntityType.PRODUCT"
                :isEditing="isEditing"
                :stekzModel="product"
                ref="textAppArtefact"
            />
        </Section>

        <Section class="processes-Section" editable>
            <Processes :isEditing="isEditing" :processes="processes" />
        </Section>

        <Section class="documents-Section" editable>
            <Documents
                v-if="product"
                entityType="product"
                :entity="product"
                :isEditing="isEditing"
                :artefacts="artefacts"
            />
        </Section>
    </Page>
</template>

<script setup lang="ts">
import Documents from "@/components/Documents.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import InfoMessage from "@/components/InfoMessage.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Processes from "@/components/Processes/ProcessesList.vue";
import Section from "@/components/Section.vue";
import { computed, ref, watch } from "vue";
import { useTaskManager } from "@/stores/task-store";
import { useProductManager } from "@/composables/product-manager";
import { useProcessesManager } from "@/stores/processes-store";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { useArtefactsManager } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";

const { artefacts } = useArtefactsManager();
useTaskManager();
const { isEditing } = useEditManager();
const { processes } = useProcessesManager();
const { product, isFetching, description } = useProductManager();
const { generateContent } = useGenerateDescriptionDialog();

const displaySpinner = ref(false);

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});

const generateProductDescription = async () => {
    if (!product.value) {
        return;
    }
    await generateContent(
        product.value.identity,
        EntityType.PRODUCT,
        description.value?.updateContent.bind(description.value)
    );
};

const hasAnyContent = computed(() => {
    return Boolean(
        processes.value.length > 0 ||
            description.value?.content ||
            artefacts.value?.length > 0
    );
});
</script>
